<template>
  <div class="sync-status-container" v-if="isSyncing">
    <div class="sync-status">
      <v-icon class="sync-icon rotating">fas fa-sync</v-icon>
      <span class="sync-text">Syncing...</span>
    </div>
  </div>
</template>

<script>
/**
 * SyncingIndicator component
 * Displays a syncing status indicator when a page is being synchronized with the backend
 * 
 * Usage:
 * <syncing-indicator page-name="page_id" :instance="{ name: 'instance_name', label: 'instance_label' }" />
 * 
 * For custom questions:
 * <syncing-indicator page-name="custom_questions" :instance="{ name: 'question', label: questionId, questionId: questionId }" />
 */
export default {
  name: 'SyncingIndicator',
  props: {
    /**
     * The page name/ID to check syncing status for
     */
    pageName: {
      type: String,
      required: true,
      default: ''
    },
    /**
     * Optional instance object for pages that can have multiple instances
     * Regular instances should have { name, label }
     * Custom questions should have { name: 'question', label: questionId, questionId }
     */
    instance: {
      type: Object,
      default: null
    }
  },
  computed: {
    /**
     * Whether the page is currently syncing (status = IN_PROGRESS)
     */
    isSyncing() {
      const page = this.findPage();
      return page && page.status === 'IN_PROGRESS';
    }
  },
  methods: {
    /**
     * Find the progress page corresponding to current page/instance
     * @returns {Object|null} The progress page object or null if not found
     */
    findPage() {
      try {
        if (!this.hasValidStoreData()) {
          return null;
        }
        
        const progress = this.$store.getters.clientProgress.progress;
        
        // Find matching page based on page type
        if (this.isCustomQuestion()) {
          return this.findCustomQuestionPage(progress);
        } else if (this.instance) {
          return this.findInstancePage(progress);
        } else {
          return this.findRegularPage(progress);
        }
      } catch (error) {
        console.error('SyncingIndicator: Error finding page:', error);
        return null;
      }
    },
    
    /**
     * Check if the component is being used for a custom question
     */
    isCustomQuestion() {
      return this.instance && this.instance.questionId;
    },
    
    /**
     * Find a custom question page from progress data
     */
    findCustomQuestionPage(progress) {
      const questionId = this.instance.questionId;
      return progress.find(p => 
        p.pageName === this.pageName && 
        p.data && 
        p.data.question && 
        p.data.question._id === questionId
      );
    },
    
    /**
     * Find a page with instance (like accounts) from progress data
     */
    findInstancePage(progress) {
      return progress.find(p => 
        p.pageName === this.pageName && 
        p.instance === this.instance.name && 
        p.label === this.instance.label
      );
    },
    
    /**
     * Find a regular page without instance from progress data
     */
    findRegularPage(progress) {
      return progress.find(p => p.pageName === this.pageName);
    },
    
    /**
     * Check if we have valid store data to check sync status
     */
    hasValidStoreData() {
      return this.pageName &&
        this.$store && 
        this.$store.getters && 
        this.$store.getters.clientProgress && 
        this.$store.getters.clientProgress.progress &&
        Array.isArray(this.$store.getters.clientProgress.progress);
    }
  }
};
</script>

<style lang="scss" scoped>
.sync-status-container {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.sync-status {
  display: flex;
  align-items: center;
  background-color: rgba(33, 150, 243, 0.1);
  border: 1px solid rgba(33, 150, 243, 0.3);
  border-radius: 4px;
  padding: 8px 16px;
}

.sync-icon {
  color: #2196f3;
  margin-right: 8px;
}

.sync-text {
  color: #2196f3;
  font-weight: 500;
}

.rotating {
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style> 